/**
 * Add a member from the admin directory (POST api/members). Before creating, checks for a likely
 * duplicate (GET api/members/check-duplicate: same first name + surname and the same email or
 * cellphone) and offers to open it instead.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconUserPlus, IconAlertTriangle, IconAlertCircle, IconExternalLink } from '@tabler/icons-react';
import { Modal, Stack, Group, Button, TextInput, Select, SimpleGrid, Switch, Alert, Text, DatePickerInput } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useMemberService, type Member } from '@actschurch/ccms-sdk';
import {
  GENDER_OPTIONS, MEMBER_DIRECTORY_KEY, dateToDob, errorMessage, memberFullName, textOrNull, useMaritalStatuses, useMemberTypes,
} from '@/components/members';

interface Props { opened: boolean; onClose: () => void }

interface Form {
  firstName: string;
  lastName: string;
  email: string;
  cellNumber: string;
  gender: string | null;
  dateOfBirth: Date | null;
  maritalStatusID: string | null;
  typeID: string | null;
  isMember: boolean;
}

const EMPTY: Form = { firstName: '', lastName: '', email: '', cellNumber: '', gender: null, dateOfBirth: null, maritalStatusID: null, typeID: null, isMember: false };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CreateMemberModal({ opened, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memberService = useMemberService();
  const { options: typeOptions } = useMemberTypes();
  const { options: maritalOptions } = useMaritalStatuses();
  const [form, setForm] = useState<Form>(EMPTY);
  const [duplicate, setDuplicate] = useState<Member | null>(null);
  const [checkedKey, setCheckedKey] = useState<string | null>(null);

  const dupKey = [form.firstName, form.lastName, form.email, form.cellNumber].map((s) => s.trim().toLowerCase()).join('|');

  const close = () => { setForm(EMPTY); setDuplicate(null); setCheckedKey(null); create.reset(); onClose(); };

  const create = useMutation({
    mutationFn: async (skipDuplicateCheck: boolean) => {
      if (!skipDuplicateCheck || checkedKey !== dupKey) {
        const dup = await memberService.checkDuplicate(form.firstName.trim(), form.lastName.trim(), textOrNull(form.email), textOrNull(form.cellNumber));
        setCheckedKey(dupKey);
        if (dup && !dup.isDeleted) { setDuplicate(dup); return null; }
      }
      const body: Member = {
        memberID: 0,
        userId: '',
        firstName: form.firstName.trim(),
        middleName: null,
        lastName: textOrNull(form.lastName),
        dateOfBirth: dateToDob(form.dateOfBirth),
        gender: form.gender,
        maritalStatusID: form.maritalStatusID ? Number(form.maritalStatusID) : 0,
        email: form.email.trim(),
        cellNumber: textOrNull(form.cellNumber),
        altContact: null,
        telephone: null,
        isMember: form.isMember,
        isActive: true,
        avatar: null,
        typeID: form.typeID ? Number(form.typeID) : 0,
        memberCode: null,
      };
      return memberService.create(body);
    },
    onSuccess: (created) => {
      if (!created) return;
      toast.success('Member added', `${memberFullName(created) || 'The member'} has been added.`);
      queryClient.invalidateQueries({ queryKey: MEMBER_DIRECTORY_KEY });
      close();
      navigate(`/admin/members/${created.memberID}`);
    },
    onError: (e) => toast.error('Could not add the member', errorMessage(e)),
  });

  const emailError = form.email.trim() && !EMAIL_RE.test(form.email.trim()) ? 'Enter a valid email address' : null;
  const canSave = form.firstName.trim().length > 0 && form.lastName.trim().length > 0 && !emailError;
  const showDuplicate = duplicate && checkedKey === dupKey;

  return (
    <Modal opened={opened} onClose={close} title="Add a member" size="lg" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">For visitors, children and anyone without a login. People who sign up get a member record automatically.</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="First name" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.currentTarget.value })} />
          <TextInput label="Surname" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.currentTarget.value })} />
          <TextInput label="Email" value={form.email} error={emailError} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
          <TextInput label="Cellphone number" value={form.cellNumber} onChange={(e) => setForm({ ...form, cellNumber: e.currentTarget.value })} />
          <Select label="Gender" placeholder="Choose" data={GENDER_OPTIONS} clearable value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} />
          <DatePickerInput label="Date of birth" placeholder="Pick a date" clearable maxDate={new Date()} valueFormat="D MMMM YYYY"
            value={form.dateOfBirth} onChange={(v) => setForm({ ...form, dateOfBirth: (v as Date | null) ?? null })} />
          <Select label="Marital status" placeholder="Choose" data={maritalOptions} clearable value={form.maritalStatusID} onChange={(v) => setForm({ ...form, maritalStatusID: v })} />
          <Select label="Member type" placeholder="Choose" data={typeOptions} clearable value={form.typeID} onChange={(v) => setForm({ ...form, typeID: v })} />
        </SimpleGrid>
        <Switch label="Church member" description="Leave off for visitors." checked={form.isMember} onChange={(e) => setForm({ ...form, isMember: e.currentTarget.checked })} />

        {showDuplicate && duplicate && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="This person may already exist">
            <Stack gap="xs">
              <Text size="sm">
                {memberFullName(duplicate) || `Member ${duplicate.memberID}`} (#{duplicate.memberID}) has the same name and {duplicate.email && duplicate.email.toLowerCase() === form.email.trim().toLowerCase() ? 'email' : 'cellphone number'}.
              </Text>
              <Group gap="xs">
                <Button size="xs" variant="light" leftSection={<IconExternalLink size={14} />} onClick={() => { const id = duplicate.memberID; close(); navigate(`/admin/members/${id}`); }}>
                  Open existing member
                </Button>
                <Button size="xs" variant="subtle" color="orange" loading={create.isPending} onClick={() => create.mutate(true)}>Add anyway</Button>
              </Group>
            </Stack>
          </Alert>
        )}
        {create.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(create.error)}</Alert>}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button leftSection={<IconUserPlus size={16} />} disabled={!canSave} loading={create.isPending && !showDuplicate} onClick={() => create.mutate(false)}>
            Add member
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
